import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Download, Info } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import InfoModal from "../components/InfoModal";

// Helper function to group entries by quarter
const groupByQuarter = (entries) => {
  const quarters = {};
  entries.forEach(entry => {
    const date = new Date(entry.date);
    const year = date.getFullYear();
    const quarter = Math.floor((date.getMonth()) / 3) + 1;
    const key = `${year}-Q${quarter}`;
    
    if (!quarters[key]) {
      quarters[key] = [];
    }
    quarters[key].push(entry);
  });
  
  // Sort quarters descending (newest first)
  return Object.keys(quarters)
    .sort((a, b) => b.localeCompare(a))
    .map(key => ({
      quarter: key,
      entries: quarters[key].sort((a, b) => new Date(b.date) - new Date(a.date))
    }));
};

export default function Werbeartikel() {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [formData, setFormData] = useState({
    article: "",
    date: new Date().toISOString().split('T')[0],
    quantity: "1",
    customer_name: "",
    anlass: ""
  });

  useEffect(() => {
    loadEntries();
    loadProducts();
  }, []);

  const loadEntries = async () => {
    try {
      const response = await axios.get(`${API}/werbeartikel`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const loadProducts = async () => {
    try {
      // Only load geschäftlich stock (werbeartikel are always business-related)
      const response = await axios.get(`${API}/stock?type=${encodeURIComponent('geschäftlich')}`);
      // Filter products with stock > 0
      const availableProducts = response.data.filter(product => product.stock > 0);
      setProducts(availableProducts);
    } catch (error) {
      toast.error("Fehler beim Laden der Produkte");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/werbeartikel`, {
        ...formData,
        quantity: parseInt(formData.quantity)
      });
      
      toast.success("Werbeartikel erfasst!");
      setFormData({
        article: "",
        date: new Date().toISOString().split('T')[0],
        quantity: "1",
        customer_name: "",
        anlass: ""
      });
      setShowForm(false);
      loadEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/werbeartikel/${id}`);
      toast.success("Eintrag gelöscht");
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const requestDownloadPDF = (quarter) => {
    setPendingDownload(quarter);
    setShowDownloadWarning(true);
  };

  const confirmDownload = async () => {
    if (!pendingDownload) return;
    const quarter = pendingDownload;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/werbeartikel/export/${quarter}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Werbeartikel_${quarter}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF wird heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        <Link to="/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Werbeartikel</h1>
                <p className="text-slate-500 mt-1 text-sm sm:text-base">Werbeartikel erfassen (reduziert Bestand)</p>
              </div>
              <Button 
                onClick={() => setShowInfoModal(true)}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200 w-full sm:w-auto"
              >
                <Info className="w-4 h-4 mr-2" />
                Kategorien-Hilfe
              </Button>
            </div>
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Werbeartikel hinzufügen
            </Button>
          </div>

          {showForm && (
            <div className="mb-8 p-6 bg-cyan-50 rounded-lg border border-cyan-200">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                Neuer Werbeartikel
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="article">Artikel *</Label>
                    <Select 
                      value={formData.article} 
                      onValueChange={(value) => setFormData({...formData, article: value})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Artikel auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.article} value={product.article}>
                            {product.article} (Bestand: {product.stock})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="quantity">Menge *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="date">Datum *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_name">Empfänger/Firma *</Label>
                    <Input
                      id="customer_name"
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                      placeholder="Firma/Person"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="anlass">Anlass/Zweck *</Label>
                  <Input
                    id="anlass"
                    type="text"
                    value={formData.anlass}
                    onChange={(e) => setFormData({...formData, anlass: e.target.value})}
                    placeholder="z.B. Messe, Kundenbesuch, Marketing"
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700">
                    Speichern
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Abbrechen
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="space-y-8">
            {groupByQuarter(entries).length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Noch keine Werbeartikel erfasst
              </div>
            )}
            
            {groupByQuarter(entries).map((quarterGroup) => (
              <div key={quarterGroup.quarter} className="bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex justify-between items-center p-4 bg-slate-100 rounded-t-lg border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {quarterGroup.quarter.replace('Q', '. Quartal ')}
                  </h3>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => requestDownloadPDF(quarterGroup.quarter)}
                    className="bg-white hover:bg-slate-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF Export
                  </Button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Datum</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Artikel</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Empfänger/Firma</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Menge</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Anlass/Zweck</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">EK-Gesamt</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterGroup.entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-slate-100 hover:bg-white">
                          <td className="py-3 px-4">{new Date(entry.date).toLocaleDateString('de-DE')}</td>
                          <td className="py-3 px-4">{entry.article}</td>
                          <td className="py-3 px-4">{entry.customer_name}</td>
                          <td className="py-3 px-4 text-right">{entry.quantity}</td>
                          <td className="py-3 px-4">{entry.anlass}</td>
                          <td className="py-3 px-4 text-right font-semibold">{entry.ek_total.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right">
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-b-lg border-t border-slate-200">
                  <div className="text-sm text-slate-600">
                    <strong>Quartal Zusammenfassung:</strong> {quarterGroup.entries.length} Werbeartikel, 
                    Gesamtwert: <span className="font-semibold text-cyan-600">
                      {quarterGroup.entries.reduce((sum, entry) => sum + entry.ek_total, 0).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <InfoModal 
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          category="werbung"
        />
      </div>
    </div>
  );
}